<script setup lang="ts">
/**
 * Personal Knowledge Base manager (ADR 0019 §P5).
 *
 * Left rail: list of KBs the user owns + KBs shared with them, plus
 * a "Create KB" form. Right pane: the selected KB's documents (with
 * status pills + drag-drop / file-picker upload) and its share list
 * (add/remove grantees).
 *
 * Style: matches ContractsDashboard.vue — plain HTML + scoped CSS, no
 * PrimeVue components at this layer. Keeps the bundle small and the
 * page understandable.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { useConfirm } from "../composables/useConfirm";
import {
  createKb,
  deleteKb,
  deleteKbDocument,
  getKb,
  listKbs,
  revokeKbShare,
  shareKb,
  uploadKbDocument,
  type Kb,
  type KbDetail,
  type KbUploadMode,
} from "../api/kbs";
import { BookOpen } from "lucide-vue-next";
import SidePanel from "../components/SidePanel.vue";

const props = defineProps<{
  devUser: string;
  /** The signed-in user's Entra OID — what `owner_oid` on the KB row
   *  is set to. In real-auth mode `devUser` is "" so we can't compare
   *  against it; this prop is the real identity for partitioning
   *  mine-vs-shared and gating owner-only management. */
  myOid: string;
  /** Knowledge Base sharing, switched by the deployment (KB_SHARING_ENABLED).
   *  Off until it is supported end to end; the server refuses it as well. */
  sharingEnabled?: boolean;
}>();
const emit = defineEmits<{ (e: "exit"): void }>();

const { confirm } = useConfirm();

// Template ref to the create-KB name input, focused by `openCreate` once
// the drawer is up.
//
// There was a `focusCreateForm()` here that only focused the input. That
// worked while the create form sat inline on the page; when it moved into
// the drawer, focusing a field inside a closed drawer became a silent
// no-op — so "Create your first KB" in the empty state did nothing at all,
// while the identical rail button worked. Both now call `openCreate`.
const newNameInput = ref<HTMLInputElement | null>(null);

const kbs = ref<Kb[]>([]);
const selectedId = ref<number | null>(null);
const selectedDetail = ref<KbDetail | null>(null);
const loadingList = ref(false);
const loadingDetail = ref(false);
const errorMsg = ref<string>("");
const status = ref<string>("");

// Create-KB form
// Creating a KB is a rare act; browsing them is the constant one. The form
// used to sit permanently expanded above the list, pushing it down. It now
// opens in the same drawer the admin editor uses.
const createOpen = ref(false);
function openCreate(): void {
  createOpen.value = true;
  nextTick(() => newNameInput.value?.focus());
}

const newName = ref<string>("");
const newDesc = ref<string>("");
const creating = ref(false);

// Share form — typed input can be either an email or an Entra OID.
// The backend resolves emails via Graph; OIDs (GUIDs) go through
// unchanged. Detection is just a GUID regex.
const granteeInput = ref<string>("");
const sharing = ref(false);
const GUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// Partition on the real Entra OID (real-auth) OR the dev-user string
// (dev-auth) — whichever is set. Falling back keeps the dev-mode flow
// working unchanged.
const meId = computed(() => props.myOid || props.devUser);
const owned = computed(() =>
  kbs.value.filter((k) => k.owner_oid === meId.value),
);
const shared = computed(() =>
  kbs.value.filter((k) => k.owner_oid !== meId.value),
);

const totalDocs = computed(() => selectedDetail.value?.documents.length ?? 0);
const readyDocs = computed(
  () =>
    selectedDetail.value?.documents.filter((d) => d.status === "ready")
      .length ?? 0,
);

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function note(msg: string) {
  status.value = msg;
  errorMsg.value = "";
  if (msg)
    setTimeout(() => {
      if (status.value === msg) status.value = "";
    }, 4000);
}

function fail(msg: string) {
  errorMsg.value = msg;
  status.value = "";
}

async function refreshList() {
  loadingList.value = true;
  try {
    kbs.value = await listKbs(props.devUser);
  } catch (e) {
    fail(`Could not load your KBs: ${(e as Error).message}`);
  } finally {
    loadingList.value = false;
  }
}

async function refreshDetail() {
  if (selectedId.value === null) {
    selectedDetail.value = null;
    return;
  }
  loadingDetail.value = true;
  try {
    selectedDetail.value = await getKb(props.devUser, selectedId.value);
  } catch (e) {
    selectedDetail.value = null;
    fail(`Could not load KB: ${(e as Error).message}`);
  } finally {
    loadingDetail.value = false;
  }
}

// ── Indexing progress ──────────────────────────────────────────────────────
// KB indexing is asynchronous: the upload returns immediately and
// caj-choir-kb-indexer-eu flips each row to `ready` or `failed` later. The
// page fetched the detail exactly once, right after upload — before any of
// that had happened — so rows sat at "pending" forever and the only way to
// learn the outcome was to reselect the KB or reload. Drop a folder of 30
// files and you got 30 frozen rows.
//
// The page also promises "you'll get an e-mail when indexing finishes" —
// but only when the upload response says so (`notify_by_email`): the
// backend sets it when service mail is configured (NOTIFY_MAIL_FROM,
// ADR 0019 §6) and the document went to the async worker. Otherwise no
// mail is sent, so none is promised, and the page keeps itself up to date.
const POLL_MS = 4000;
/** Give up after ~10 minutes so a stuck document can't poll forever. */
const POLL_MAX_MS = 10 * 60_000;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollStartedAt = 0;

const TERMINAL = new Set(["ready", "failed"]);
const indexingCount = computed(
  () =>
    (selectedDetail.value?.documents ?? []).filter(
      (d) => !TERMINAL.has(d.status),
    ).length,
);

function stopPolling(): void {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

/** Poll while anything is still indexing; stop as soon as all rows settle. */
function ensurePolling(): void {
  if (pollTimer !== null) return;
  if (indexingCount.value === 0) return;
  pollStartedAt = Date.now();
  const tick = async () => {
    pollTimer = null;
    if (selectedId.value === null) return;
    if (Date.now() - pollStartedAt > POLL_MAX_MS) {
      note("Still indexing — reload to check again.");
      return;
    }
    await refreshDetail();
    await refreshList(); // doc_count moves as documents land
    if (indexingCount.value > 0) {
      pollTimer = setTimeout(tick, POLL_MS);
    }
  };
  pollTimer = setTimeout(tick, POLL_MS);
}

// Any refresh that leaves work outstanding re-arms the timer; selecting a
// different KB drops the old one.
watch(indexingCount, (n) => {
  if (n > 0) ensurePolling();
  else stopPolling();
});
watch(selectedId, () => stopPolling());
onBeforeUnmount(stopPolling);

async function select(kbId: number) {
  selectedId.value = kbId;
  await refreshDetail();
}

onMounted(async () => {
  await refreshList();
});

async function onCreate() {
  if (!newName.value.trim()) {
    fail("Give the KB a name.");
    return;
  }
  creating.value = true;
  try {
    const kb = await createKb(props.devUser, {
      name: newName.value.trim(),
      description: newDesc.value.trim(),
    });
    newName.value = "";
    newDesc.value = "";
    // Close the drawer on success. It stayed open over the KB that had just
    // been created and selected behind it, which reads as "nothing
    // happened" — the same drawer-refactor oversight as the empty-state CTA
    // above. Only on success: a failed create must keep the form and what
    // was typed into it.
    createOpen.value = false;
    note(`Created "${kb.name}".`);
    await refreshList();
    await select(kb.id);
  } catch (e) {
    fail(`Create failed: ${(e as Error).message}`);
  } finally {
    creating.value = false;
  }
}

async function onDelete(kbId: number, name: string) {
  const ok = await confirm({
    title: "Delete knowledge base?",
    message: `Delete KB "${name}"? Documents and shares are removed too. This cannot be undone.`,
    confirmLabel: "Delete",
    danger: true,
  });
  if (!ok) return;
  try {
    await deleteKb(props.devUser, kbId);
    note(`Deleted "${name}".`);
    if (selectedId.value === kbId) {
      selectedId.value = null;
      selectedDetail.value = null;
    }
    await refreshList();
  } catch (e) {
    fail(`Delete failed: ${(e as Error).message}`);
  }
}

// ── Extraction mode ───────────────────────────────────────────────────────
// `fast` is Document Intelligence: managed, no cold start, so a folder of
// 200 files starts converting immediately. `thorough` is docling on the GPU,
// which scales up from zero first (minutes) and reads scans and dense layout
// better. Default fast — the common case is ordinary office documents, where
// the wait dominates the quality difference, and the GPU bills for every
// minute it is awake.
const uploadMode = ref<KbUploadMode>("fast");

// ── Upload queue ──────────────────────────────────────────────────────────
// A folder upload is not "a few files": one drop can be hundreds. The old
// handler fired every upload at once, which for a folder would open hundreds
// of concurrent requests. Work through a bounded queue instead, and report
// progress as one line rather than one toast per file.
const UPLOAD_CONCURRENCY = 4;

/** Files that exist to serve the filesystem, not the user. Uploading these
 *  from a folder would create junk KB documents that someone then has to
 *  delete by hand. */
const JUNK_FILES = new Set([
  ".ds_store",
  "thumbs.db",
  "desktop.ini",
  "icon\r",
]);

function isUploadable(file: File, relPath: string): boolean {
  const base = (file.name || "").toLowerCase();
  if (!base || JUNK_FILES.has(base)) return false;
  // Dotfiles and anything inside a dot-directory (.git, .venv, .obsidian…).
  if (relPath.split("/").some((seg) => seg.startsWith("."))) return false;
  // A directory entry surfaces as a zero-byte File in some browsers.
  if (file.size === 0) return false;
  return true;
}

type Pending = { file: File; relPath: string };

const uploadTotal = ref(0);
const uploadDone = ref(0);
const uploadFailed = ref(0);
const uploadBusy = ref(false);
const uploadCancelled = ref(false);
/** Set when any upload in this batch will end in a "ready" e-mail. */
const uploadWillMail = ref(false);
const uploadCurrent = ref("");

const uploadProgressLabel = computed(() => {
  if (!uploadBusy.value) return "";
  const n = uploadDone.value + uploadFailed.value;
  return `Uploading ${n + 1} of ${uploadTotal.value}…`;
});

function cancelUpload() {
  uploadCancelled.value = true;
}

/** Upload one file. Returns true on success. Never throws — a folder
 *  upload must not stop at the first unreadable file. */
async function uploadOne(item: Pending): Promise<boolean> {
  if (selectedId.value === null) return false;
  try {
    const res = await uploadKbDocument(props.devUser, selectedId.value, item.file, {
      mode: uploadMode.value,
      relativePath: item.relPath,
    });
    if (res.notify_by_email) uploadWillMail.value = true;
    return true;
  } catch (e) {
    // Logged rather than toasted: 200 failures would be 200 toasts. The
    // count is surfaced in the summary and each doc's status pill.
    console.warn(`KB upload failed for ${item.relPath || item.file.name}`, e);
    return false;
  }
}

async function runUploadQueue(items: Pending[]) {
  if (selectedId.value === null || items.length === 0) return;
  uploadBusy.value = true;
  uploadCancelled.value = false;
  uploadTotal.value = items.length;
  uploadDone.value = 0;
  uploadFailed.value = 0;
  uploadWillMail.value = false;

  const queue = [...items];
  async function worker() {
    for (;;) {
      if (uploadCancelled.value) return;
      const next = queue.shift();
      if (!next) return;
      uploadCurrent.value = next.relPath || next.file.name;
      const ok = await uploadOne(next);
      if (ok) uploadDone.value += 1;
      else uploadFailed.value += 1;
    }
  }

  try {
    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker),
    );
  } finally {
    uploadBusy.value = false;
    uploadCurrent.value = "";
    const ok = uploadDone.value;
    const bad = uploadFailed.value;
    const stopped = uploadCancelled.value;
    if (bad === 0 && !stopped) {
      const mail = uploadWillMail.value
        ? " You'll get an e-mail when indexing finishes."
        : "";
      note(
        (ok === 1
          ? "Uploaded 1 file — indexing in the background."
          : `Uploaded ${ok} files — indexing in the background.`) + mail,
      );
    } else if (stopped) {
      note(`Stopped after ${ok} file${ok === 1 ? "" : "s"}.`);
    } else {
      fail(`Uploaded ${ok}, failed ${bad}. See the browser console for detail.`);
    }
    await refreshDetail();
    await refreshList(); // doc_count on list view
  }
}

function onFileInputChange(ev: Event) {
  const target = ev.target as HTMLInputElement;
  const picked = Array.from(target.files ?? []).map((f) => ({
    file: f,
    // webkitRelativePath is set by the directory picker and empty otherwise.
    relPath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
  }));
  target.value = "";
  const items = picked.filter((p) => isUploadable(p.file, p.relPath));
  const skipped = picked.length - items.length;
  if (skipped > 0) {
    note(`Skipped ${skipped} system file${skipped === 1 ? "" : "s"}.`);
  }
  void runUploadQueue(items);
}

// ── Drag-and-drop, including folders ──────────────────────────────────────
// `dataTransfer.files` flattens a dropped folder to nothing useful, so walk
// the entry tree instead. webkitGetAsEntry is the only cross-browser way in;
// it is prefixed but implemented everywhere we support.
function entryFile(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (f) => resolve(f),
      () => resolve(null),
    );
  });
}

/** readEntries returns at most ~100 entries per call and signals the end
 *  with an empty batch — a single call silently truncates a large folder. */
function readAllEntries(
  dir: FileSystemDirectoryEntry,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => {
    const reader = dir.createReader();
    const all: FileSystemEntry[] = [];
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(all);
            return;
          }
          all.push(...batch);
          readBatch();
        },
        () => resolve(all),
      );
    };
    readBatch();
  });
}

const MAX_DROP_FILES = 2000;

async function collectEntry(
  entry: FileSystemEntry,
  prefix: string,
  out: Pending[],
): Promise<void> {
  if (out.length >= MAX_DROP_FILES) return;
  const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const f = await entryFile(entry as FileSystemFileEntry);
    if (f && isUploadable(f, relPath)) out.push({ file: f, relPath });
    return;
  }
  if (entry.isDirectory) {
    if (entry.name.startsWith(".")) return;
    const children = await readAllEntries(entry as FileSystemDirectoryEntry);
    for (const child of children) {
      await collectEntry(child, relPath, out);
    }
  }
}

const dragActive = ref(false);
function onDragOver(ev: DragEvent) {
  ev.preventDefault();
  dragActive.value = true;
}
function onDragLeave() {
  dragActive.value = false;
}
async function onDrop(ev: DragEvent) {
  ev.preventDefault();
  dragActive.value = false;

  const items = Array.from(ev.dataTransfer?.items ?? []);
  const entries = items
    .map((it) => it.webkitGetAsEntry?.() ?? null)
    .filter((e): e is FileSystemEntry => e !== null);

  let pending: Pending[] = [];
  if (entries.length > 0) {
    for (const entry of entries) await collectEntry(entry, "", pending);
  } else {
    // Browser without the entries API — plain files still work, folders do not.
    pending = Array.from(ev.dataTransfer?.files ?? [])
      .map((f) => ({ file: f, relPath: f.name }))
      .filter((p) => isUploadable(p.file, p.relPath));
  }

  if (pending.length >= MAX_DROP_FILES) {
    note(`Taking the first ${MAX_DROP_FILES} files — drop the rest separately.`);
  }
  void runUploadQueue(pending);
}

async function onDocDelete(docId: string, filename: string) {
  if (selectedId.value === null) return;
  const ok = await confirm({
    title: "Remove document?",
    message: `Remove "${filename}" from this KB?`,
    confirmLabel: "Remove",
    danger: true,
  });
  if (!ok) return;
  try {
    await deleteKbDocument(props.devUser, selectedId.value, docId);
    note(`Removed ${filename}.`);
    await refreshDetail();
    await refreshList();
  } catch (e) {
    fail(`Delete failed: ${(e as Error).message}`);
  }
}

async function onShare() {
  if (selectedId.value === null) return;
  const raw = granteeInput.value.trim();
  if (!raw) return;
  sharing.value = true;
  try {
    const grantee = GUID_RE.test(raw) ? { oid: raw } : { email: raw };
    await shareKb(props.devUser, selectedId.value, grantee);
    granteeInput.value = "";
    note("Shared.");
    await refreshDetail();
  } catch (e) {
    fail(`Share failed: ${(e as Error).message}`);
  } finally {
    sharing.value = false;
  }
}

async function onRevoke(granteeOid: string) {
  if (selectedId.value === null) return;
  const ok = await confirm({
    title: "Revoke access?",
    message: `Revoke access for ${granteeOid}?`,
    confirmLabel: "Revoke",
    danger: true,
  });
  if (!ok) return;
  try {
    await revokeKbShare(props.devUser, selectedId.value, granteeOid);
    note("Revoked.");
    await refreshDetail();
  } catch (e) {
    fail(`Revoke failed: ${(e as Error).message}`);
  }
}
</script>

<template>
  <div class="kb-mgr">
    <header class="kb-mgr__head">
      <h2>My Knowledge Bases</h2>
      <button class="kb-mgr__exit" type="button" @click="emit('exit')">
        ← Back
      </button>
    </header>

    <p v-if="errorMsg" class="kb-mgr__error">{{ errorMsg }}</p>
    <p v-if="status" class="kb-mgr__status">{{ status }}</p>

    <div class="kb-mgr__body">
      <!-- ── Left rail: list + create ───────────────────────────── -->
      <aside class="kb-mgr__rail">
        <button type="button" class="kb-mgr__new" @click="openCreate">
          <span aria-hidden="true">+</span> New knowledge base
        </button>

        <section class="kb-mgr__rail-section">
          <h3>Yours ({{ owned.length }})</h3>
          <p v-if="loadingList" class="kb-mgr__loading">Loading…</p>
          <p v-else-if="owned.length === 0" class="kb-mgr__muted">
            Nothing here yet.
          </p>
          <ul v-else class="kb-mgr__list">
            <li
              v-for="kb in owned"
              :key="kb.id"
              :class="{ 'kb-mgr__list-item--active': selectedId === kb.id }"
            >
              <button
                type="button"
                class="kb-mgr__list-btn"
                @click="select(kb.id)"
              >
                <span class="kb-mgr__list-name">{{ kb.name }}</span>
                <span class="kb-mgr__list-meta">
                  {{ kb.doc_count }} {{ kb.doc_count === 1 ? "doc" : "docs" }}
                </span>
              </button>
              <button
                type="button"
                class="kb-mgr__list-delete"
                title="Delete KB"
                @click="onDelete(kb.id, kb.name)"
              >
                ✕
              </button>
            </li>
          </ul>
        </section>

        <section v-if="props.sharingEnabled && shared.length > 0" class="kb-mgr__rail-section">
          <h3>Shared with me ({{ shared.length }})</h3>
          <ul class="kb-mgr__list">
            <li
              v-for="kb in shared"
              :key="kb.id"
              :class="{ 'kb-mgr__list-item--active': selectedId === kb.id }"
            >
              <button
                type="button"
                class="kb-mgr__list-btn"
                @click="select(kb.id)"
              >
                <span class="kb-mgr__list-name">{{ kb.name }}</span>
                <span class="kb-mgr__list-meta"> by {{ kb.owner_oid }} </span>
              </button>
            </li>
          </ul>
        </section>
      </aside>

      <!-- ── Right pane: selected KB ───────────────────────────── -->
      <main class="kb-mgr__pane">
        <div v-if="selectedId === null" class="kb-mgr__empty">
          <span class="kb-mgr__empty-icon" aria-hidden="true">
            <BookOpen :size="26" :stroke-width="1.7" />
          </span>
          <h3 class="kb-mgr__empty-title">
            {{
              owned.length === 0
                ? "Create your first knowledge base"
                : "Pick a knowledge base"
            }}
          </h3>
          <p class="kb-mgr__empty-lead">
            Your own documents, private unless you share them.
          </p>
          <button
            v-if="owned.length === 0"
            type="button"
            class="kb-mgr__empty-cta"
            @click="openCreate"
          >
            Create your first KB
          </button>
          <p v-else class="kb-mgr__muted">
            Select one from the left rail, or create a new one.
          </p>
        </div>

        <template v-else-if="selectedDetail">
          <header class="kb-mgr__pane-head">
            <h3>{{ selectedDetail.name }}</h3>
            <p v-if="selectedDetail.description" class="kb-mgr__desc">
              {{ selectedDetail.description }}
            </p>
            <p class="kb-mgr__meta">
              Owner: {{ selectedDetail.owner_oid }} · {{ readyDocs }}/{{
                totalDocs
              }}
              ready ·
              {{ formatBytes(selectedDetail.byte_count) }}
            </p>
          </header>

          <!-- Documents -->
          <section class="kb-mgr__docs">
            <div class="kb-mgr__docs-head">
              <h4>Documents</h4>
              <!-- Visible progress, so a folder drop doesn't look frozen
                   while the worker chews through it. -->
              <span v-if="indexingCount > 0" class="kb-mgr__indexing">
                <span class="kb-mgr__indexing-dot" aria-hidden="true"></span>
                {{ indexingCount }} indexing…
              </span>
              <div
                class="kb-mgr__mode"
                role="radiogroup"
                aria-label="Processing speed"
              >
                <button
                  type="button"
                  role="radio"
                  :aria-checked="uploadMode === 'fast'"
                  :class="{ 'is-on': uploadMode === 'fast' }"
                  :disabled="uploadBusy"
                  title="Document Intelligence — starts immediately. Best for ordinary documents."
                  @click="uploadMode = 'fast'"
                >
                  Fast
                </button>
                <button
                  type="button"
                  role="radio"
                  :aria-checked="uploadMode === 'thorough'"
                  :class="{ 'is-on': uploadMode === 'thorough' }"
                  :disabled="uploadBusy"
                  title="Docling on the GPU — a few minutes to start up. Better on scans and dense layouts."
                  @click="uploadMode = 'thorough'"
                >
                  Thorough
                </button>
              </div>
              <label
                class="kb-mgr__upload-btn"
                :class="{ 'is-disabled': uploadBusy }"
              >
                Upload files
                <input
                  type="file"
                  multiple
                  hidden
                  :disabled="uploadBusy"
                  @change="onFileInputChange"
                />
              </label>
              <label
                class="kb-mgr__upload-btn"
                :class="{ 'is-disabled': uploadBusy }"
              >
                Upload folder
                <!-- webkitdirectory is prefixed but is the only way to pick a
                     directory; it is implemented in every browser we support.
                     `multiple` stays so the same input still accepts a
                     multi-select if the directory picker is unavailable. -->
                <input
                  type="file"
                  webkitdirectory
                  multiple
                  hidden
                  :disabled="uploadBusy"
                  @change="onFileInputChange"
                />
              </label>
            </div>

            <div v-if="uploadBusy" class="kb-mgr__uploading">
              <div class="kb-mgr__uploading-bar" aria-hidden="true">
                <span
                  :style="{
                    width:
                      Math.round(
                        ((uploadDone + uploadFailed) / uploadTotal) * 100,
                      ) + '%',
                  }"
                ></span>
              </div>
              <span class="kb-mgr__uploading-text">
                {{ uploadProgressLabel }}
                <span v-if="uploadCurrent" class="kb-mgr__uploading-file">{{
                  uploadCurrent
                }}</span>
              </span>
              <button
                type="button"
                class="kb-mgr__uploading-stop"
                @click="cancelUpload"
              >
                Stop
              </button>
            </div>

            <div
              class="kb-mgr__drop"
              :class="{ 'kb-mgr__drop--active': dragActive }"
              @dragover="onDragOver"
              @dragleave="onDragLeave"
              @drop="onDrop"
            >
              <p v-if="selectedDetail.documents.length === 0">
                Drag files or a whole folder here, or use the buttons above.
                Subfolders are included. Supports PDF, Word, PowerPoint, Excel,
                Markdown, and plain text.
              </p>
              <table v-else class="kb-mgr__table">
                <colgroup>
                  <col class="kb-mgr__col-name" />
                  <col class="kb-mgr__col-status" />
                  <col class="kb-mgr__col-size" />
                  <col class="kb-mgr__col-action" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in selectedDetail.documents" :key="d.id">
                    <td class="kb-mgr__fname" :title="d.filename">
                      {{ d.filename }}
                    </td>
                    <td>
                      <span
                        class="kb-mgr__pill"
                        :class="`kb-mgr__pill--${d.status}`"
                        :title="d.error ?? ''"
                      >
                        {{ d.status }}
                      </span>
                    </td>
                    <td>{{ formatBytes(d.byte_count) }}</td>
                    <td>
                      <button
                        type="button"
                        class="kb-mgr__row-delete"
                        @click="onDocDelete(d.id, d.filename)"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Shares (owner-only management) -->
          <section
            v-if="props.sharingEnabled && selectedDetail.owner_oid === meId"
            class="kb-mgr__shares"
          >
            <h4>Sharing</h4>
            <form class="kb-mgr__share-form" @submit.prevent="onShare">
              <input
                v-model="granteeInput"
                type="text"
                placeholder="Email (or Entra OID)"
                maxlength="320"
              />
              <button type="submit" :disabled="sharing">
                {{ sharing ? "Sharing…" : "Grant access" }}
              </button>
            </form>

            <ul
              v-if="selectedDetail.shares.length > 0"
              class="kb-mgr__share-list"
            >
              <li v-for="s in selectedDetail.shares" :key="s.grantee_oid">
                <span>{{ s.grantee_oid }}</span>
                <button
                  type="button"
                  class="kb-mgr__row-delete"
                  @click="onRevoke(s.grantee_oid)"
                >
                  Revoke
                </button>
              </li>
            </ul>
            <p v-else class="kb-mgr__muted">Not shared with anyone yet.</p>
          </section>
        </template>

        <p v-else-if="loadingDetail" class="kb-mgr__loading">Loading…</p>
      </main>
    </div>
  </div>
  <SidePanel
    v-model="createOpen"
    title="New knowledge base"
    width="min(32rem, 92vw)"
  >
    <div class="kb-mgr__create">
      <label>
        <span>Name</span>
        <input
          ref="newNameInput"
          v-model="newName"
          type="text"
          placeholder="e.g. Project Apollo notes"
          maxlength="255"
        />
      </label>
      <label>
        <span>Description (optional)</span>
        <textarea
          v-model="newDesc"
          rows="2"
          placeholder="What's in here?"
          maxlength="2000"
        ></textarea>
      </label>
    </div>
    <template #actions>
      <button class="btn" type="button" @click="createOpen = false">
        Cancel
      </button>
      <button class="btn btn--primary" :disabled="creating" @click="onCreate">
        {{ creating ? "Creating…" : "Create" }}
      </button>
    </template>
  </SidePanel>
</template>

<style scoped>
/* Definite width (not max-width): the page is an auto-margin grid item in the
   app-shell, so `max-width` alone lets it collapse to its *content* width —
   which made the whole layout jump wider when a KB with a document table was
   selected. `width: min(...)` pins it so the width is content-independent. */
.kb-mgr {
  width: min(1200px, 100%);
  margin-inline: auto;
}
.kb-mgr__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-3);
}
.kb-mgr__head h2 {
  margin: 0;
}
.kb-mgr__exit {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  color: var(--color-ink);
}
.kb-mgr__error {
  background: #fde2e1;
  color: #841914;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin-bottom: var(--space-3);
}
.kb-mgr__status {
  background: #e7f5e8;
  color: #1c5f24;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin-bottom: var(--space-3);
}
.kb-mgr__body {
  /* `minmax(0, 1fr)` (not bare `1fr`) so a wide document table is clipped to
     its share instead of pushing the whole grid wider. */
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: var(--space-4);
}
@media (max-width: 768px) {
  .kb-mgr__body {
    grid-template-columns: 1fr;
  }
}
.kb-mgr__rail-section {
  margin-bottom: var(--space-4);
}
.kb-mgr__rail-section h3 {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-muted);
  margin-bottom: var(--space-2);
}
.kb-mgr__create {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
}
.kb-mgr__create label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85rem;
}
.kb-mgr__create input,
.kb-mgr__create textarea {
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
}
.kb-mgr__create button {
  align-self: flex-start;
  padding: 0.5rem 0.9rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}
.kb-mgr__create button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.kb-mgr__loading,
.kb-mgr__muted {
  color: var(--color-muted);
  font-size: 0.85rem;
}
.kb-mgr__list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.kb-mgr__list li {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--color-border);
}
.kb-mgr__list-item--active {
  background: color-mix(in srgb, var(--color-accent) 8%, transparent);
}
.kb-mgr__list-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--space-2);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--color-ink);
}
.kb-mgr__list-btn:hover {
  background: color-mix(in srgb, var(--color-accent) 4%, transparent);
}
.kb-mgr__list-name {
  font-weight: 500;
}
.kb-mgr__list-meta {
  font-size: 0.75rem;
  color: var(--color-muted);
}
.kb-mgr__new {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: 100%;
  padding: 0.5rem 0.9rem;
  border-radius: var(--radius-pill);
  border: 1px solid color-mix(in srgb, var(--color-accent) 22%, transparent);
  background: color-mix(in srgb, var(--color-accent) 9%, transparent);
  color: var(--color-accent);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-1) var(--ease-out);
}
.kb-mgr__new:hover {
  background: color-mix(in srgb, var(--color-accent) 16%, transparent);
}
/* Destructive, so revealed on hover/focus rather than resident on every row
   — and kept visible where there is no hover to reveal it. */
.kb-mgr__list-delete {
  opacity: 0;
  transition: opacity var(--dur-1) var(--ease-out);
}
.kb-mgr__list li:hover .kb-mgr__list-delete,
.kb-mgr__list-delete:focus-visible {
  opacity: 1;
}
@media (hover: none) {
  .kb-mgr__list-delete {
    opacity: 1;
  }
}
.kb-mgr__list-delete {
  background: none;
  border: none;
  padding: 0 var(--space-2);
  cursor: pointer;
  color: var(--color-muted);
  font-size: 0.9rem;
}
.kb-mgr__list-delete:hover {
  color: #cc1f1a;
}
.kb-mgr__pane {
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  min-height: 400px;
  /* Force full-width fill of the grid cell — without this the
     empty-state placeholder caused the pane to shrink-to-content,
     so the "Pick a KB on the left…" box rendered noticeably
     narrower than the selected-KB view. box-sizing keeps the
     padding inside the declared width. */
  width: 100%;
  box-sizing: border-box;
}
.kb-mgr__placeholder {
  color: var(--color-muted);
}
/* Onboarding empty-state — fills the pane with real guidance so the
   box reads at full size and a first-time user knows what to do. */
.kb-mgr__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-2);
  max-width: 460px;
  margin: 0 auto;
  padding: var(--space-6) var(--space-3);
}
.kb-mgr__empty-icon {
  font-size: 2.4rem;
  line-height: 1;
}
.kb-mgr__empty-title {
  margin: 0;
  font-size: 1.15rem;
  color: var(--color-ink);
}
.kb-mgr__empty-lead {
  margin: 0;
  color: var(--color-muted);
  line-height: 1.55;
}
.kb-mgr__empty-formats {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.kb-mgr__empty-cta {
  margin-top: var(--space-2);
  padding: 0.55rem 1.1rem;
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
}
.kb-mgr__empty-cta:hover {
  background: var(--color-accent-hover);
}
.kb-mgr__pane-head h3 {
  margin: 0 0 var(--space-1);
}
.kb-mgr__desc {
  color: var(--color-muted);
  margin: 0 0 var(--space-1);
}
.kb-mgr__meta {
  color: var(--color-muted);
  font-size: 0.8rem;
  margin: 0 0 var(--space-4);
}
/* space-between alone was enough when this row held a heading and one
   button. With the mode toggle and two upload buttons the controls collide
   on the right, so they need their own spacing — and room to wrap rather
   than squeeze on a narrow window. */
.kb-mgr__docs-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}
/* A slightly wider gap before the upload buttons separates "how to process"
   from "what to upload" without needing a divider. */
.kb-mgr__mode + .kb-mgr__upload-btn {
  margin-left: var(--space-1, 0.25rem);
}
.kb-mgr__indexing {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-right: auto;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.kb-mgr__indexing-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--color-accent);
  animation: kb-indexing-pulse 1.4s ease-in-out infinite;
}
@keyframes kb-indexing-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .kb-mgr__indexing-dot {
    animation: none;
    opacity: 0.7;
  }
}
.kb-mgr__docs-head h4 {
  margin: 0;
}
.kb-mgr__upload-btn {
  display: inline-block;
  padding: 0.4rem 0.8rem;
  background: var(--color-accent);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
}
/* Two upload buttons now sit side by side; the folder one is the rarer
   action, so it reads as secondary rather than a second primary. */
.kb-mgr__upload-btn + .kb-mgr__upload-btn {
  background: transparent;
  color: var(--color-accent);
  box-shadow: inset 0 0 0 1px var(--color-accent);
}
.kb-mgr__upload-btn.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Fast / Thorough segmented control. */
.kb-mgr__mode {
  display: inline-flex;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px var(--color-border);
  overflow: hidden;
}
.kb-mgr__mode button {
  padding: 0.4rem 0.7rem;
  font-size: 0.8rem;
  border: 0;
  background: transparent;
  color: var(--color-text-muted, inherit);
  cursor: pointer;
}
.kb-mgr__mode button.is-on {
  background: var(--color-accent);
  color: white;
}
.kb-mgr__mode button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* One progress line for the whole batch — a folder upload must not emit a
   toast per file. */
.kb-mgr__uploading {
  display: flex;
  align-items: center;
  gap: var(--space-2, 0.5rem);
  margin-bottom: var(--space-3);
  font-size: 0.85rem;
}
.kb-mgr__uploading-bar {
  position: relative;
  flex: 1 1 auto;
  height: 4px;
  border-radius: 2px;
  background: var(--color-border);
  overflow: hidden;
}
.kb-mgr__uploading-bar span {
  display: block;
  height: 100%;
  background: var(--color-accent);
  transition: width 0.2s ease-out;
}
.kb-mgr__uploading-text {
  flex: 0 0 auto;
  display: inline-flex;
  gap: 0.4rem;
  align-items: baseline;
}
.kb-mgr__uploading-file {
  max-width: 18ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.7;
}
.kb-mgr__uploading-stop {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  color: var(--color-text-muted, inherit);
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.8rem;
}
.kb-mgr__drop {
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  padding: var(--space-3);
  margin-bottom: var(--space-4);
  transition:
    border-color 0.15s,
    background 0.15s;
}
.kb-mgr__drop--active {
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 6%, transparent);
}
/* Fixed layout so column widths are stable regardless of filename length
   (auto layout reflowed the whole table per KB — poor UX). The filename
   column takes the remaining space and truncates with an ellipsis; the
   rest are fixed. */
.kb-mgr__table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.kb-mgr__col-name {
  width: auto;
}
.kb-mgr__col-status {
  width: 7rem;
}
.kb-mgr__col-size {
  width: 5.5rem;
}
.kb-mgr__col-action {
  width: 5rem;
}
.kb-mgr__table th,
.kb-mgr__table td {
  text-align: left;
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid var(--color-border);
}
.kb-mgr__table th {
  color: var(--color-muted);
  font-weight: 500;
}
/* Truncate long filenames with an ellipsis (full name in the title
   tooltip) instead of letting them stretch / wrap the layout. */
.kb-mgr__fname {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kb-mgr__pill {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  background: var(--color-border);
  color: var(--color-ink);
}
.kb-mgr__pill--ready {
  background: #d1f0d6;
  color: #1c5f24;
}
.kb-mgr__pill--pending,
.kb-mgr__pill--indexing {
  background: #fff0c2;
  color: #6e5800;
}
.kb-mgr__pill--failed {
  background: #fde2e1;
  color: #841914;
}
.kb-mgr__row-delete {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-muted);
  font-size: 0.85rem;
}
.kb-mgr__row-delete:hover {
  color: #cc1f1a;
  text-decoration: underline;
}
.kb-mgr__share-form {
  display: flex;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}
.kb-mgr__share-form input {
  flex: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.9rem;
}
.kb-mgr__share-form button {
  padding: 0.4rem 0.8rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}
.kb-mgr__share-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.kb-mgr__share-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.9rem;
}
</style>
